import { AuthEvent } from "@/features/Auth/AuthPopup/utils/constants";
import { useUpgradePopupContextSelector } from "@/features/Payments/PaywallPopup/UpgradePopupContext";
import { useCheckAuth } from "@/hooks/useCheckAuth";
import { assertTruthy } from "@/utils/assertions";
import { AI_TOOLS_FORM_INPUTS, DEFAULT_INPUTS_VALUES } from "@knowt/syncing/constants/ai-tools";
import { useS3UploadJobsSelector } from "@knowt/syncing/context/S3UploadJobContext";
import { AICompletionType, ItemType, TranscriptEnum, UpgradeEvent } from "@knowt/syncing/graphql/schema";
import { useStreamingAI } from "@knowt/syncing/hooks/ai/useAI";
import { useAITeacherTools } from "@knowt/syncing/hooks/ai/useAITeacherTools";
import { callAddMediaS3UserTag } from "@knowt/syncing/hooks/media/graphqlUtils";
import { useMedia } from "@knowt/syncing/hooks/media/useMedia";
import { useCurrentUser } from "@knowt/syncing/hooks/user/useCurrentUser";
import { TOOL_URL, checkEmptyMandatoryInputs } from "@knowt/syncing/utils/aiTools";
import { useRouter } from "@bprogress/next";
import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { v4 as uuidv4 } from "uuid";
import { AI_PROMPT_OUTPUT_MAP, TEACHER_TOOLS_OUTPUT_FORMATTER } from "@knowt/syncing/hooks/ai/utils";
import { mdToHtml } from "@knowt/editor/md/parsing/parse";
import { createNewNote } from "@knowt/syncing/hooks/notes/utils";
import { genericUpdateMedias } from "@knowt/syncing/hooks/media/utils";

export const useToolSettings = ({
    tool,
    folderId,
    classId,
}: {
    tool: keyof AI_PROMPT_OUTPUT_MAP;
    folderId?: string;
    classId?: string;
}) => {
    const noteId = useMemo(() => uuidv4(), []);
    const { userId, user, hasReachedAIUsage } = useCurrentUser();
    const openUpgradePopup = useUpgradePopupContextSelector(s => s.openUpgradePopup);
    const { checkAuth } = useCheckAuth();
    const router = useRouter();

    const confirmUpload = useS3UploadJobsSelector(state => state.confirmUpload);
    const clearUpload = useS3UploadJobsSelector(state => state.clearUpload);
    const uploadProgress = useS3UploadJobsSelector(state => state.uploadProgress);
    const mediaId = useS3UploadJobsSelector(state => state?.uploadedFileId);

    const { media, isPollingForTranscript } = useMedia({
        mediaId,
        pollForTranscripts: !!mediaId,
        isEnabled: !!mediaId,
    });

    const [settingsValues, setSettingsValues] = useState(DEFAULT_INPUTS_VALUES);
    const [droppedFile, setDroppedFile] = useState<File | null>(null);
    const [isUploadingFile, setIsUploadingFile] = useState<boolean>(false);
    const [isShowingMissingInputs, setIsShowingMissingInputs] = useState<boolean>(false);
    const [isGeneratingNote, setIsGeneratingNote] = useState<boolean>(false);

    const { filteredCleanedSettingsPerTool, filteredSettingsPerTool } = useAITeacherTools({
        settings: settingsValues,
        tool,
    });

    const mandatoryInputs = Object.values(AI_TOOLS_FORM_INPUTS[tool] || {})
        .filter(input => input.required)
        .map(input => input.name);

    const { callAI } = useStreamingAI({
        itemId: "",
        itemType: ItemType.NOTE,
        type: AICompletionType.TEACHER_TOOL_FIELDS_HELPER,
    });

    const { callAI: callAIForNoteGeneration } = useStreamingAI({
        itemId: noteId,
        itemType: ItemType.NOTE,
        type: tool,
    });

    const handleOnChange = (name, value) => {
        setSettingsValues(prev => ({ ...prev, [name]: value }));
    };

    const handleStopUploadFile = () => {
        if (uploadProgress) clearUpload();
        setIsUploadingFile(false);
        setDroppedFile(null);
    };

    const handleOnClear = () => {
        setSettingsValues(DEFAULT_INPUTS_VALUES);
        handleStopUploadFile();
    };

    const _handleOnGenerate = async () => {
        const isAuthenticated = await checkAuth({
            event: AuthEvent.LANDING_TEACHER_TOOLS,
        });

        if (!isAuthenticated) return;

        if (isUploadingFile) {
            return toast.error("Please wait for your file to be uploaded");
        }

        if (checkEmptyMandatoryInputs(filteredSettingsPerTool, mandatoryInputs)) {
            setIsShowingMissingInputs(true);
            return toast.error("Please fill in the mandatory fields");
        }

        if (hasReachedAIUsage("tools")) {
            openUpgradePopup({
                event: UpgradeEvent.MAX_AI_TOOLS,
                context: {},
            });
            return;
        }

        if (media?.transcript === TranscriptEnum.FREE) {
            openUpgradePopup({
                event: UpgradeEvent.MAX_PDF_AI,
                context: {},
            });
            return;
        }

        setIsGeneratingNote(true);

        const generationSettings = {
            ...filteredCleanedSettingsPerTool,
            mediaId,
        };

        const { response } = await callAIForNoteGeneration({
            aiParams: {
                ...generationSettings,
                files: generationSettings.mediaId
                    ? [{ itemId: generationSettings.mediaId, itemType: ItemType.MEDIA }]
                    : undefined,
                messages: [],
            },
        });

        const parsedOutput = TEACHER_TOOLS_OUTPUT_FORMATTER<typeof tool>(tool, JSON.parse(response));

        await createNewNote(
            {
                ...parsedOutput,
                content: mdToHtml(parsedOutput.content),
                aiTool: tool,
                isAI: true,
                draft: true,
                mediaId: generationSettings.mediaId ?? undefined,
                noteId: noteId,
                folderId,
                classId,
            },
            user
        );

        if (generationSettings.mediaId) {
            await genericUpdateMedias({
                mediaIds: [{ userId: userId, mediaId: generationSettings.mediaId }],
                userId,
                updatedFields: {
                    noteId: noteId,
                },
            });
        }

        setIsGeneratingNote(false);
        router.push(`/free-ai-tools-for-teachers/${TOOL_URL[tool]}/${noteId}`);
    };

    const handleHelpFillField = async (description: string, inputName: string) => {
        try {
            const { response } = await callAI({
                aiParams: {
                    tool: AICompletionType[tool],
                    description: description,
                    fields: [],
                },
            });

            const responseObject = JSON.parse(response);
            return responseObject[inputName];
        } catch (e) {
            if (e.message === "Over TEACHER_TOOL_FIELDS_HELPER usage limit") {
                return toast.error("You have exceeded the usage limit for the Teacher Tool Helper feature.");
            }

            toast.error("Something went wrong please try again.");
        }
    };

    const handleUploadFile = async (data: { name?: string; file: File; extension?: string }) => {
        if (!user) {
            return;
        }

        try {
            assertTruthy(data.file, "file should be defined");

            if (uploadProgress) clearUpload();

            if (hasReachedAIUsage("videos") || hasReachedAIUsage("pdfs")) {
                return openUpgradePopup({
                    event: UpgradeEvent.MAX_PDF_AI,
                    context: {},
                });
            }

            const { id, extension, bucket, cancelled } = await confirmUpload({
                file: data.file,
            });
            await callAddMediaS3UserTag({
                mediaId: `${id}.${extension}`,
                bucket,
                startProcessing: true,
            });

            if (cancelled) {
                toast.error("Upload cancelled");
                return;
            }
        } catch {
            toast.error("Upload failed, please try again after reloading the page.");
        } finally {
            setIsUploadingFile(false);
        }
    };

    useEffect(() => {
        if (!droppedFile) return;

        checkAuth({
            event: AuthEvent.LANDING_TEACHER_TOOLS,
            onAuthSuccess: () => {
                setIsUploadingFile(true);
                handleUploadFile({
                    name: droppedFile.name,
                    file: droppedFile,
                    extension: droppedFile.name.split(".")[1],
                });
            },
        });
    }, [droppedFile]);

    useEffect(() => {
        if (isShowingMissingInputs) {
            setIsShowingMissingInputs(false);
        }
    }, [settingsValues]);

    return {
        settingsValues,
        setSettingsValues,
        droppedFile,
        setDroppedFile,
        handleOnChange,
        handleOnClear,
        handleOnGenerate: _handleOnGenerate,
        handleHelpFillField,
        isUploadingFile,
        setIsUploadingFile,
        handleStopUploadFile,
        isGeneratingNote,
        isPollingForTranscript,
        mediaId,
        mandatoryInputs,
        isShowingMissingInputs,
    };
};
